import axios from 'axios';
import { env, isMockMode } from '@/config/env';
import { useAppStore } from '@/stores/app-store';
import { API_ROUTES } from '@/config/constants';

export const { isAxiosError } = axios;

export const api = axios.create({
  baseURL: env.VITE_API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
  timeout: 15000,
});

// ─── Request interceptor ──────────────────────────────────────────────────────

api.interceptors.request.use((config) => {
  const token = sessionStorage.getItem('access_token');
  const appState = useAppStore.getState();
  const mockUserId = sessionStorage.getItem('mock_user_id') ?? appState.currentUser?.id;
  const selectedWholesalerId =
    appState.selectedWholesalerId ?? appState.currentUser?.wholesalers[0]?.id;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  if (isMockMode && mockUserId) {
    config.headers['x-mock-user-id'] = mockUserId;
  }
  const isAdminRoute = config.url?.startsWith('/admin') ?? false;
  // A call site that sets the header itself owns the scope. That includes an explicit `null`,
  // which axios drops from the outgoing request — the way a call that must NOT inherit the
  // currently-selected workspace opts out (e.g. the signup wizard's SetupIntent, where the
  // wholesaler being set up does not exist yet).
  const hasCallerScope = config.headers['x-wholesaler-id'] !== undefined;
  if (!isAdminRoute && !hasCallerScope && selectedWholesalerId) {
    config.headers['x-wholesaler-id'] = selectedWholesalerId;
  }
  return config;
});

// ─── Refresh queue ────────────────────────────────────────────────────────────

interface QueueEntry {
  resolve: (token: string) => void;
  reject: (err: unknown) => void;
}

let isRefreshing = false;
let failedQueue: QueueEntry[] = [];

const processQueue = (error: unknown, token: string | null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error !== null) {
      reject(error);
    } else if (token !== null) {
      resolve(token);
    }
  });
  failedQueue = [];
};

// ─── Response interceptor ─────────────────────────────────────────────────────

interface RetryableConfig {
  _retry?: boolean;
}

api.interceptors.response.use(
  (response) => response,
  async (error: unknown) => {
    if (!axios.isAxiosError(error) || error.response?.status !== 401) {
      return Promise.reject(error instanceof Error ? error : new Error(String(error)));
    }

    const originalRequest = error.config;

    // Prevent infinite loop if refresh itself returns 401
    if (!originalRequest || (originalRequest as RetryableConfig)._retry) {
      sessionStorage.removeItem('access_token');
      sessionStorage.removeItem('mock_user_id');
      window.dispatchEvent(new CustomEvent('auth:expired'));
      return Promise.reject(error instanceof Error ? error : new Error(String(error)));
    }

    if (isRefreshing) {
      return new Promise<string>((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then((token) => {
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return api(originalRequest);
      });
    }

    (originalRequest as RetryableConfig)._retry = true;
    isRefreshing = true;

    try {
      // Refresh is cookie-based (withCredentials) — the backend's TokenResponse carries no
      // refresh token, so there is nothing to read from or write back to storage here.
      const response = await axios.post<{ accessToken: string }>(
        `${env.VITE_API_BASE_URL}${API_ROUTES.auth.refresh}`,
        {},
        { withCredentials: true },
      );

      const { accessToken } = response.data;
      sessionStorage.setItem('access_token', accessToken);

      processQueue(null, accessToken);

      originalRequest.headers.Authorization = `Bearer ${accessToken}`;
      return await api(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError, null);
      sessionStorage.removeItem('access_token');
      sessionStorage.removeItem('mock_user_id');
      window.dispatchEvent(new CustomEvent('auth:expired'));
      return await Promise.reject(
        refreshError instanceof Error ? refreshError : new Error(String(refreshError)),
      );
    } finally {
      isRefreshing = false;
    }
  },
);
